// Enhanced type definitions for Mentra platform

export type Organization = {
  id: string;
  name: string;
  slug: string;
  avatar_url: string | null;
  settings: Record<string, any>;
  created_at: string;
  updated_at: string;
};

export type OrganizationMember = {
  id: string;
  organization_id: string;
  user_id: string;
  role: 'owner' | 'admin' | 'manager' | 'member';
  permissions: string[];
  joined_at: string;
};

export type Subscription = {
  id: string;
  user_id: string | null;
  organization_id: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  plan_type: 'free' | 'professional' | 'team' | 'enterprise';
  status: 'active' | 'past_due' | 'canceled' | 'incomplete' | 'trialing';
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
};

export type UsageTracking = {
  id: string;
  user_id: string;
  organization_id: string | null;
  period_start: string;
  period_end: string;
  requests_sent: number;
  requests_completed: number;
  ai_analyses_used: number;
  storage_used_mb: number;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
};

export type EmailNotification = {
  id: string;
  user_id: string | null;
  feedback_request_id: string | null;
  notification_type: 'feedback_request' | 'reminder' | 'confirmation' | 'weekly_digest' | 'feedback_received';
  recipient_email: string;
  subject: string | null;
  status: 'pending' | 'sent' | 'delivered' | 'opened' | 'failed' | 'bounced';
  sent_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  error_message: string | null;
  metadata: Record<string, any>;
  created_at: string;
};

export type FeedbackTag = {
  id: string;
  user_id: string;
  name: string;
  color: string;
  created_at: string;
};

export type NotificationPreferences = {
  id: string;
  user_id: string;
  email_on_feedback_received: boolean;
  email_on_reminder: boolean;
  email_weekly_digest: boolean;
  email_marketing: boolean;
  in_app_notifications: boolean;
  created_at: string;
  updated_at: string;
};

export type AuditLog = {
  id: string;
  user_id: string | null;
  organization_id: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  ip_address: string | null;
  user_agent: string | null;
  metadata: Record<string, any>;
  created_at: string;
};

// Plan limits
export const PLAN_LIMITS = {
  free: {
    requests_per_month: 5,
    ai_analyses_per_month: 3,
    storage_mb: 100,
    team_members: 1,
    features: ['basic_feedback', 'voice_recording', 'text_feedback']
  },
  professional: {
    requests_per_month: 50,
    ai_analyses_per_month: 50,
    storage_mb: 5000,
    team_members: 1,
    features: ['all_free', 'ai_insights', 'pdf_export', 'custom_branding', 'priority_support']
  },
  team: {
    requests_per_month: 200,
    ai_analyses_per_month: 200,
    storage_mb: 20000,
    team_members: 10,
    features: ['all_professional', 'team_workspace', 'manager_dashboard', 'bulk_requests', 'advanced_analytics']
  },
  enterprise: {
    requests_per_month: -1, // unlimited
    ai_analyses_per_month: -1,
    storage_mb: -1,
    team_members: -1,
    features: ['all_team', 'custom_integrations', 'dedicated_support', 'sso', 'custom_contracts']
  }
};

// Plan pricing
export const PLAN_PRICING = {
  free: { monthly: 0, annual: 0 },
  professional: { monthly: 29, annual: 290 }, // ~17% discount
  team: { monthly: 99, annual: 990 },
  enterprise: { monthly: 499, annual: 4990 }
};
